import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import { normalizeExpiryToEndOfDayUTC, validateMcpKeyName } from '../services/mcpKeyHelpers';
import { startRegistration } from '@simplewebauthn/browser';

export default function AccountSecurityPage() {
  const { user, logout, loadUser } = useAuth();
  const navigate = useNavigate();

  // Global message
  const [message, setMessage] = useState({ text: '', type: '' });

  // Profile
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '' });
  const [profileLoading, setProfileLoading] = useState(false);

  // Password
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // TOTP
  const [totpStatus, setTotpStatus] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [totpLoading, setTotpLoading] = useState(false);

  // Passkeys
  const [passkeys, setPasskeys] = useState([]);
  const [passkeyName, setPasskeyName] = useState('');
  const [addingPasskey, setAddingPasskey] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  // Connected apps
  const [applications, setApplications] = useState([]);

  // Sessions
  const [sessions, setSessions] = useState([]);

  // MCP API keys
  const [mcpKeys, setMcpKeys] = useState([]);
  const [showMcpForm, setShowMcpForm] = useState(false);
  const [mcpForm, setMcpForm] = useState({ keyName: '', expiresAt: '' });
  const [mcpFormError, setMcpFormError] = useState('');
  const [mcpLoading, setMcpLoading] = useState(false);
  // Holds the full secret ONLY while the reveal-once modal is open; cleared on dismiss.
  const [revealedMcpKey, setRevealedMcpKey] = useState(null);

  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  }, []);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', email: user.email || '' });
    }
    apiClient.get('/api/totp/status').then(setTotpStatus).catch(() => {});
    apiClient.get('/api/passkeys').then(data => setPasskeys(data.passkeys || [])).catch(() => {});
    apiClient.get('/api/auth/applications').then(data => setApplications(data.applications || [])).catch(() => {});
    apiClient.get('/api/auth/sessions').then(data => setSessions(data.sessions || [])).catch(() => {});
    apiClient.listMcpKeys().then(data => setMcpKeys(data.apiKeys || [])).catch(() => {});
  }, [user]);

  // SPA deep-link fix: this is a client-rendered CRA page, so a URL fragment
  // (e.g. #mcp-keys) does not exist in the initial static HTML — the browser's
  // native load-time fragment-scroll runs before React mounts the target element
  // and finds nothing. Re-check the hash after mount and scroll manually.
  useEffect(() => {
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // --- Profile ---
  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const updates = {};
      if (profileForm.name !== user.name) updates.name = profileForm.name;
      if (profileForm.email !== user.email) updates.email = profileForm.email;
      if (Object.keys(updates).length === 0) {
        setEditingProfile(false);
        setProfileLoading(false);
        return;
      }
      const data = await apiClient.put('/api/auth/profile', updates);
      showMessage(data.message || 'Profile updated');
      setEditingProfile(false);
      await loadUser();
    } catch (err) {
      showMessage(err.message, 'error');
    }
    setProfileLoading(false);
  };

  // --- Password ---
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage('New passwords do not match', 'error');
      return;
    }
    setPasswordLoading(true);
    try {
      const data = await apiClient.put('/api/auth/password', passwordForm);
      showMessage(data.message || 'Password changed');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (err) {
      showMessage(err.message, 'error');
    }
    setPasswordLoading(false);
  };

  // --- TOTP ---
  const startTotpSetup = async () => {
    setTotpLoading(true);
    try {
      const data = await apiClient.post('/api/totp/setup');
      setTotpSetup(data);
      setTotpVerifyCode('');
      setBackupCodes(null);
    } catch (err) {
      showMessage(err.message, 'error');
    }
    setTotpLoading(false);
  };

  const verifyTotpSetup = async (e) => {
    e.preventDefault();
    setTotpLoading(true);
    try {
      const data = await apiClient.post('/api/totp/verify-setup', { token: totpVerifyCode });
      setBackupCodes(data.backupCodes);
      setTotpSetup(null);
      setTotpVerifyCode('');
      setTotpStatus({ enabled: true, backupCodesRemaining: data.backupCodes.length });
      showMessage('Two-factor authentication enabled');
    } catch (err) {
      showMessage(err.message, 'error');
    }
    setTotpLoading(false);
  };

  const disableTotp = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication?')) return;
    setTotpLoading(true);
    try {
      await apiClient.post('/api/totp/disable');
      setTotpStatus({ enabled: false });
      setTotpSetup(null);
      setBackupCodes(null);
      showMessage('Two-factor authentication disabled');
    } catch (err) {
      showMessage(err.message, 'error');
    }
    setTotpLoading(false);
  };

  const regenerateBackupCodes = async () => {
    if (!window.confirm('This will invalidate all existing backup codes. Continue?')) return;
    setTotpLoading(true);
    try {
      const data = await apiClient.post('/api/totp/regenerate-backup-codes');
      setBackupCodes(data.backupCodes);
      setTotpStatus(prev => ({ ...prev, backupCodesRemaining: data.backupCodes.length }));
      showMessage('Backup codes regenerated');
    } catch (err) {
      showMessage(err.message, 'error');
    }
    setTotpLoading(false);
  };

  // --- Passkeys ---
  const addPasskey = async () => {
    setPasskeyLoading(true);
    try {
      const options = await apiClient.post('/api/passkeys/register/options');
      const attResp = await startRegistration({ optionsJSON: options });
      attResp.friendlyName = passkeyName || 'My Passkey';
      await apiClient.post('/api/passkeys/register/verify', attResp);
      showMessage('Passkey registered');
      setAddingPasskey(false);
      setPasskeyName('');
      const data = await apiClient.get('/api/passkeys');
      setPasskeys(data.passkeys || []);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        showMessage('Passkey registration was cancelled', 'error');
      } else {
        showMessage(err.message, 'error');
      }
    }
    setPasskeyLoading(false);
  };

  const deletePasskey = async (id) => {
    if (!window.confirm('Delete this passkey?')) return;
    try {
      await apiClient.del(`/api/passkeys/${id}`);
      setPasskeys(prev => prev.filter(p => p.id !== id));
      showMessage('Passkey deleted');
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  // --- Sessions ---
  const revokeSession = async (id) => {
    try {
      await apiClient.del(`/api/auth/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      showMessage('Session revoked');
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  // --- MCP API keys ---
  const refreshMcpKeys = async () => {
    const data = await apiClient.listMcpKeys();
    setMcpKeys(data.apiKeys || []);
  };

  const createMcpKey = async (e) => {
    e.preventDefault();
    // Client-side validation mirrors the backend 400 (name 1–100 chars, no whitespace-only).
    const nameValidation = validateMcpKeyName(mcpForm.keyName);
    if (!nameValidation.valid) {
      setMcpFormError(nameValidation.error);
      return;
    }
    setMcpFormError('');
    setMcpLoading(true);
    try {
      const body = { keyName: nameValidation.trimmed };
      // Date-only picker returns "YYYY-MM-DD"; normalise to end-of-day UTC so the
      // key is valid through 23:59:59 of the day the user selected (convention: UTC end-of-day).
      // Returns null when no date selected — field is then omitted from the POST body (R49.5).
      const expiry = normalizeExpiryToEndOfDayUTC(mcpForm.expiresAt);
      if (expiry) {
        body.expiresAt = expiry;
      }
      const data = await apiClient.createMcpKey(body);
      // Show the full secret exactly once; never store it in the list state.
      setRevealedMcpKey(data.apiKey);
      setShowMcpForm(false);
      setMcpForm({ keyName: '', expiresAt: '' });
      await refreshMcpKeys();
    } catch (err) {
      // Surface backend validation/error messages inline.
      setMcpFormError(err.message);
    }
    setMcpLoading(false);
  };

  const dismissRevealedKey = () => {
    // After dismissal the full key is unrecoverable from the UI.
    setRevealedMcpKey(null);
  };

  const revokeMcpKey = async (id) => {
    if (!window.confirm('Revoke this MCP key? Any client using it will stop working immediately. This cannot be undone.')) return;
    try {
      await apiClient.revokeMcpKey(id);
      await refreshMcpKeys();
      showMessage('MCP key revoked');
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  // --- Logout ---
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const hasPassword = user?.password_hash !== undefined ? !!user.password_hash : true;

  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="account-page">
      {/* Header */}
      <header className="account-header">
        <div className="account-header-inner">
          <Link to="/account" className="account-brand">
            <span className="account-brand-raike">Raike</span>
            <span className="account-brand-amp">&amp;</span>
            <span className="account-brand-sons">Sons</span>
          </Link>
          <nav className="account-nav">
            {user?.role === 'admin' && (
              <Link to="/admin" className="account-nav-link">Admin</Link>
            )}
            <button className="account-nav-link" onClick={handleLogout}>
              Sign Out
            </button>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="account-content">
        <h1 className="account-title">Account Settings</h1>
        <p className="account-subtitle">Manage your profile, security, and connected services</p>

        {/* Global Message */}
        {message.text && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
            {message.text}
          </div>
        )}

        {/* Profile Section */}
        <section className="account-card">
          <div className="account-card-header">
            <h3>Profile</h3>
            {!editingProfile && (
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingProfile(true)}>Edit</button>
            )}
          </div>
          {editingProfile ? (
            <form onSubmit={handleProfileSave}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input id="name" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="account-btn-row">
                <button type="submit" className="btn btn-primary btn-sm" disabled={profileLoading}>
                  {profileLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                  setEditingProfile(false);
                  setProfileForm({ name: user?.name || '', email: user?.email || '' });
                }}>Cancel</button>
              </div>
            </form>
          ) : (
            <div className="profile-display">
              {user?.picture_url ? (
                <img src={user.picture_url} alt="" className="profile-avatar"
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div className="profile-avatar-placeholder" style={user?.picture_url ? { display: 'none' } : undefined}>{initials}</div>
              <div className="profile-info">
                <div className="profile-name">{user?.name}</div>
                <div className="profile-email">{user?.email}</div>
                <span className={`badge ${user?.role === 'admin' ? 'badge-blue' : 'badge-green'}`}>{user?.role}</span>
              </div>
            </div>
          )}
        </section>

        {/* Password Section */}
        <section className="account-card">
          <div className="account-card-header">
            <h3>Password</h3>
            {!showPasswordForm && hasPassword && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowPasswordForm(true)}>Change Password</button>
            )}
          </div>
          {!hasPassword ? (
            <p className="account-muted">
              No password set — you signed up with a social login. You can set a password via the login page.
            </p>
          ) : showPasswordForm ? (
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label htmlFor="current-password">Current Password</label>
                <input id="current-password" type="password" value={passwordForm.currentPassword}
                  onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input id="new-password" type="password" value={passwordForm.newPassword}
                  onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm New Password</label>
                <input id="confirm-password" type="password" value={passwordForm.confirmPassword}
                  onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))} required />
              </div>
              <div className="account-btn-row">
                <button type="submit" className="btn btn-primary btn-sm" disabled={passwordLoading}>
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}>Cancel</button>
              </div>
            </form>
          ) : (
            <p className="account-muted">Password is set. Click "Change Password" to update it.</p>
          )}
        </section>

        {/* TOTP Section */}
        <section className="account-card">
          <div className="account-card-header">
            <h3>Two-Factor Authentication</h3>
            {totpStatus?.enabled && !backupCodes && (
              <span className="badge badge-green">Enabled</span>
            )}
          </div>

          {backupCodes && (
            <div className="backup-codes-banner">
              <p className="backup-codes-title">
                Save your backup codes — they won't be shown again
              </p>
              <div className="backup-codes-grid">
                {backupCodes.map((code, i) => (
                  <span key={i} className="backup-code">{code}</span>
                ))}
              </div>
              <div className="account-btn-row" style={{ marginTop: '0.75rem' }}>
                <button className="btn btn-secondary btn-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(backupCodes.join('\n'));
                    showMessage('Backup codes copied to clipboard');
                  }}>Copy All</button>
                <button className="btn btn-secondary btn-sm"
                  onClick={() => setBackupCodes(null)}>Done</button>
              </div>
            </div>
          )}

          {totpStatus?.enabled ? (
            <div>
              {!backupCodes && (
                <p className="account-muted" style={{ marginBottom: '0.75rem' }}>
                  Backup codes remaining: <strong>{totpStatus.backupCodesRemaining}</strong>
                </p>
              )}
              <div className="account-btn-row">
                <button className="btn btn-secondary btn-sm"
                  onClick={regenerateBackupCodes} disabled={totpLoading}>Regenerate Backup Codes</button>
                <button className="btn btn-danger btn-sm"
                  onClick={disableTotp} disabled={totpLoading}>Disable 2FA</button>
              </div>
            </div>
          ) : totpSetup ? (
            <div>
              <p className="account-muted" style={{ marginBottom: '0.75rem' }}>
                Scan this QR code with your authenticator app:
              </p>
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <img src={totpSetup.qrCode} alt="TOTP QR Code" style={{ maxWidth: 200 }} />
              </div>
              <p className="account-muted" style={{ marginBottom: '0.75rem' }}>
                Or enter this key manually: <code className="manual-key">{totpSetup.manualEntryKey}</code>
              </p>
              <form onSubmit={verifyTotpSetup}>
                <div className="form-group">
                  <label htmlFor="totp-code">Enter the 6-digit code from your app</label>
                  <input id="totp-code" value={totpVerifyCode} onChange={e => setTotpVerifyCode(e.target.value)}
                    placeholder="000000" maxLength={6} style={{ maxWidth: 200 }} autoFocus />
                </div>
                <div className="account-btn-row">
                  <button type="submit" className="btn btn-primary btn-sm"
                    disabled={totpLoading || totpVerifyCode.length < 6}>Verify & Enable</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setTotpSetup(null)}>Cancel</button>
                </div>
              </form>
            </div>
          ) : (
            <div>
              <p className="account-muted" style={{ marginBottom: '0.75rem' }}>
                <span className="badge badge-yellow">Not enabled</span>{' '}
                Add an extra layer of security to your account.
              </p>
              <button className="btn btn-primary btn-sm"
                onClick={startTotpSetup} disabled={totpLoading}>Set Up 2FA</button>
            </div>
          )}
        </section>

        {/* Passkeys Section */}
        <section className="account-card">
          <div className="account-card-header">
            <h3>Passkeys</h3>
            {!addingPasskey && (
              <button className="btn btn-secondary btn-sm" onClick={() => setAddingPasskey(true)}>Add Passkey</button>
            )}
          </div>

          {addingPasskey && (
            <div className="account-inline-form">
              <div className="form-group">
                <label htmlFor="passkey-name">Passkey Name (optional)</label>
                <input id="passkey-name" value={passkeyName} onChange={e => setPasskeyName(e.target.value)}
                  placeholder="e.g. MacBook Pro" style={{ maxWidth: 300 }} />
              </div>
              <div className="account-btn-row">
                <button className="btn btn-primary btn-sm"
                  onClick={addPasskey} disabled={passkeyLoading}>
                  {passkeyLoading ? 'Registering...' : 'Register Passkey'}
                </button>
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setAddingPasskey(false); setPasskeyName(''); }}>Cancel</button>
              </div>
            </div>
          )}

          {passkeys.length > 0 ? (
            <ul className="account-list">
              {passkeys.map(pk => (
                <li key={pk.id} className="account-list-item">
                  <div>
                    <span className="account-list-primary">{pk.friendly_name || 'Passkey'}</span>
                    <span className="account-list-secondary"> ({pk.device_type})</span>
                    <br />
                    <span className="account-list-meta">
                      Added {new Date(pk.created_at).toLocaleDateString()}
                      {pk.last_used_at ? ` · Last used ${new Date(pk.last_used_at).toLocaleDateString()}` : ' · Never used'}
                    </span>
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => deletePasskey(pk.id)}>Delete</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="account-muted">No passkeys registered. Passkeys let you sign in with your fingerprint or face.</p>
          )}
        </section>

        {/* Connected Apps Section */}
        <section className="account-card">
          <div className="account-card-header">
            <h3>Connected Apps</h3>
          </div>
          {applications.length > 0 ? (
            <ul className="account-list">
              {applications.map(app => (
                <li key={app.id} className="account-list-item">
                  <div>
                    <span className="account-list-primary">{app.display_name || app.name}</span>
                    {app.description && (
                      <span className="account-list-secondary"> — {app.description}</span>
                    )}
                  </div>
                  <span className={`badge ${app.is_authorized ? 'badge-green' : 'badge-red'}`}>
                    {app.is_authorized ? 'Authorized' : 'Revoked'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="account-muted">No connected apps.</p>
          )}
        </section>

        {/* Active Sessions Section */}
        <section className="account-card">
          <div className="account-card-header">
            <h3>Active Sessions</h3>
          </div>
          {sessions.length > 0 ? (
            <ul className="account-list">
              {sessions.map(session => (
                <li key={session.id} className="account-list-item">
                  <div>
                    <span className="account-list-primary">{session.device_info || 'Unknown Device'}</span>
                    {session.ip_address && (
                      <span className="account-list-secondary"> ({session.ip_address})</span>
                    )}
                    <br />
                    <span className="account-list-meta">
                      Started {new Date(session.created_at).toLocaleDateString()}
                      {session.last_activity ? ` · Last active ${new Date(session.last_activity).toLocaleString()}` : ''}
                    </span>
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => revokeSession(session.id)}>Revoke</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="account-muted">No active sessions found.</p>
          )}
        </section>

        {/* MCP Keys Section */}
        <section className="account-card" id="mcp-keys">
          <div className="account-card-header">
            <h3>MCP Keys</h3>
            {!showMcpForm && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowMcpForm(true); setMcpFormError(''); }}>
                Create Key
              </button>
            )}
          </div>

          {/* Description shown only when no callout and no create form are active —
              mirrors how TOTP shows its "Add an extra layer of security" copy only in
              the not-enabled state rather than above all content. */}
          {!revealedMcpKey && !showMcpForm && mcpKeys.length === 0 && (
            <p className="account-muted" style={{ marginBottom: '0.75rem' }}>
              Use this key with your juggler / resume-optimizer MCP client.
            </p>
          )}

          {/* Reveal-once secret callout — shown exactly once, right after creation */}
          {revealedMcpKey && (
            <div className="backup-codes-banner">
              <p className="backup-codes-title">
                Copy your new key now — you won't be able to see it again
              </p>
              <p className="account-muted" style={{ marginBottom: '0.5rem' }}>
                <strong>{revealedMcpKey.key_name}</strong>
              </p>
              <code className="manual-key" style={{ display: 'block', wordBreak: 'break-all', marginBottom: '0.5rem' }}>
                {revealedMcpKey.api_key}
              </code>
              <div className="account-btn-row" style={{ marginTop: '0.75rem' }}>
                <button className="btn btn-secondary btn-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(revealedMcpKey.api_key);
                    showMessage('MCP key copied to clipboard');
                  }}>Copy Key</button>
                <button className="btn btn-primary btn-sm" onClick={dismissRevealedKey}>Done</button>
              </div>
            </div>
          )}

          {showMcpForm && (
            <div className="account-inline-form">
              <form onSubmit={createMcpKey}>
                <div className="form-group">
                  <label htmlFor="mcp-key-name">Key Name</label>
                  <input id="mcp-key-name" value={mcpForm.keyName}
                    onChange={e => setMcpForm(f => ({ ...f, keyName: e.target.value }))}
                    placeholder="e.g. Laptop MCP client" maxLength={100} style={{ maxWidth: 300 }} autoFocus />
                </div>
                <div className="form-group">
                  <label htmlFor="mcp-key-expires">Expiry (optional)</label>
                  <input id="mcp-key-expires" type="date" value={mcpForm.expiresAt}
                    onChange={e => setMcpForm(f => ({ ...f, expiresAt: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    style={{ maxWidth: 200 }} />
                </div>
                {mcpFormError && <p className="error-message" style={{ marginBottom: '0.75rem' }}>{mcpFormError}</p>}
                <div className="account-btn-row">
                  <button type="submit" className="btn btn-primary btn-sm" disabled={mcpLoading}>
                    {mcpLoading ? 'Creating...' : 'Create Key'}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => { setShowMcpForm(false); setMcpForm({ keyName: '', expiresAt: '' }); setMcpFormError(''); }}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {mcpKeys.length > 0 ? (
            <ul className="account-list">
              {mcpKeys.map(key => (
                <li key={key.id} className="account-list-item">
                  <div>
                    <span className="account-list-primary">{key.key_name}</span>
                    <span className="account-list-secondary"> ({key.key_prefix}…)</span>
                    {' '}
                    <span className={`badge ${key.status === 'active' ? 'badge-green' : key.status === 'expired' ? 'badge-yellow' : 'badge-red'}`}>
                      {key.status}
                    </span>
                    <br />
                    <span className="account-list-meta">
                      Created {new Date(key.created_at).toLocaleDateString()}
                      {key.expires_at ? ` · Expires ${new Date(key.expires_at).toLocaleDateString()}` : ' · No expiry'}
                      {key.last_used_at ? ` · Last used ${new Date(key.last_used_at).toLocaleDateString()}` : ' · Never used'}
                    </span>
                  </div>
                  {key.status === 'active' && (
                    <button className="btn btn-danger btn-sm" onClick={() => revokeMcpKey(key.id)}>Revoke</button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="account-muted">No MCP keys yet. Create one to connect an MCP client.</p>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="account-footer">
        <div className="account-footer-inner">
          <div className="account-footer-logo">Raike &amp; Sons</div>
          <div className="account-footer-tagline">Old school hustle. New school AI.</div>
          <div className="account-footer-links">
            <Link to="/login" className="account-footer-link">Sign In</Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="account-footer-link">Admin Panel</Link>
            )}
          </div>
          <div className="account-footer-est">Est. 2025</div>
        </div>
      </footer>
    </div>
  );
}
